import { createAppStore } from '@store/createAppStore';
import { initializeFeatureFlags } from '@store/featureFlags/store';
import { initializeOrderFlagStates } from '@store/orderFlagStates/store';
import { getCanUseV3RouteInjection } from '@utils/envarUtility';
import { createError } from '@utils/errorUtils';
import { routeLoginPage } from '@utils/routeLoginPage';
import { produce } from 'solid-js/store';
import { getRoutingFlags } from '@store/routingFlag/actions';
import { applyNotificationPreferencesFromSettings } from '@components/Toast/notificationPreferences';

import { fetchUser } from './services';
import { UserViewModel } from './types';

export type UserState = {
  user: UserViewModel;
  isLoading: boolean;
  isError: boolean;
  userPromise: Promise<void>;
  loggedIn: boolean;
};

const PUBLIC_ROUTES = [
  '/account/forgotpassword',
  '/reset-password',
  '/account/login',
  '/credit/apply',
  '/credit/resume',
  '/credit/submitted',
  '/credit/email-verification',
];

const PUBLIC_ROUTE_PREFIXES = ['/credit/email-verification/'];

const isPublicRoute = (pathname: string): boolean => {
  return (
    PUBLIC_ROUTES.includes(pathname) ||
    PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
};

const newUserPromise = () => {
  return new Promise<void>((resolve, reject) => {
    setTimeout(async () => {
      // Skip on mobile - mobile has its own initialization flow
      if (
        import.meta.env.VITE_IS_MOBILE === 'true' ||
        isPublicRoute(window.location.pathname)
      ) {
        return resolve();
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (userStore.user?.name !== undefined) {
        return resolve();
      }

      try {
        await initializeFeatureFlags();
        await fetchUserDetails();
        await Promise.all([getRoutingFlags(), initializeOrderFlagStates()]);
        resolve();
      } catch (error) {
        if (getCanUseV3RouteInjection()) {
          if (!isPublicRoute(window.location.pathname)) {
            if (window.location.pathname !== '/account/login') {
              const params = new URLSearchParams({
                auth_url: window.location.pathname,
              });
              window.location.href = '/account/login?' + params.toString();
            }
          }
        } else if (!isPublicRoute(window.location.pathname)) {
          if (window.location.pathname !== '/account/login') {
            routeLoginPage(window.location.pathname);
          }
        }
        reject(error);
      }
    }, 1);
  });
};

const [userStore, setUserStore] = createAppStore<UserState>(
  {
    user: {} as UserViewModel,
    isLoading: true,
    isError: false,
    loggedIn: false,
    userPromise: newUserPromise(),
  },
  'userStore',
);

// @ts-expect-error We want to expose the user store.
window.user = { userStore, setUserStore };

export const resetUserPromise = () => {
  setUserStore('userPromise', newUserPromise());
};

async function fetchUserDetails() {
  try {
    const user: UserViewModel = await fetchUser();
    applyNotificationPreferencesFromSettings(user.settings);
    setUserStore(
      produce((draft: UserState) => {
        draft.user = user;
        draft.isLoading = false;
        draft.loggedIn = true;
      }),
    );
  } catch (error) {
    setUserStore(
      produce((draft: UserState) => {
        draft.isError = true;
        draft.isLoading = false;
      }),
    );
    throw createError(error, 'Failed to fetch user details');
  }
}

export { fetchUserDetails, setUserStore, userStore };
